import { useId, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { JsonTextarea } from '@/components/atoms/JsonTextarea/JsonTextarea';

import type { CommitResult } from './ContextVariablesSection';

type JsonVariablesEditorProps = {
  json: string;
  onCommit: (text: string) => CommitResult;
  readOnly?: boolean;
};

export const JsonVariablesEditor = ({ json, onCommit, readOnly }: JsonVariablesEditorProps) => {
  const { t } = useTranslation();
  const messageId = useId();
  const [text, setText] = useState(json);
  const [committed, setCommitted] = useState(json);
  const [result, setResult] = useState<CommitResult>('applied');
  const [ownEdit, setOwnEdit] = useState(false);

  if (json !== committed) {
    setCommitted(json);
    setText(json);
    if (ownEdit) {
      setOwnEdit(false);
    } else {
      setResult('applied');
    }
    // Spent either way, so a later context change still clears the message.
  } else if (ownEdit) {
    setOwnEdit(false);
  }

  const commit = () => {
    if (readOnly) {
      setText(json);
      setResult('applied');
      return;
    }
    if (text === json) {
      setResult('applied');
      return;
    }
    const outcome = onCommit(text);
    setResult(outcome);
    setOwnEdit(outcome !== 'invalid');
    if (outcome === 'locked') {
      setText(json);
    }
  };

  const message = {
    applied: undefined,
    invalid: t('errors.invalid_json_format'),
    locked: t('try_it.context.json_locked_skipped'),
  }[result];

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <JsonTextarea
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setOwnEdit(false);
        }}
        onBlur={commit}
        readOnly={readOnly}
        aria-label={t('try_it.context.variables_label')}
        aria-invalid={result === 'invalid'}
        aria-describedby={message ? messageId : undefined}
        className={readOnly ? 'text-foreground-muted' : undefined}
      />
      {message && (
        <p id={messageId} role="alert" className="pl-1 text-xs leading-5 font-medium text-warning">
          {message}
        </p>
      )}
    </div>
  );
};
